<template>
  <UiPopup :class="$style.root" data-testid="numeric-keyboard-root" size="sm" @close="onClose">
    <h2>{{ title }}</h2>

    <input
      :class="$style.code"
      data-testid="numeric-keyboard-input"
      type="password"
      :value="inputValue"
      disabled
    />
    <div
      v-if="error"
      :class="$style.error"
      data-testid="numeric-keyboard-error"
    >
      {{ errorMessage }}
    </div>

    <div :class="$style.keypad">
      <button
        v-for="digit in 9"
        :key="digit"
        type="button"
        :class="$style.key"
        :data-testid="`numeric-keyboard-digit-${digit}`"
        @click="addDigit(digit)"
      >
        {{ digit }}
      </button>
      <button
        type="button"
        :class="[$style.key, $style.clear]"
        data-testid="numeric-keyboard-clear"
        @click="clearInput"
      >
        {{ clearLabel }}
      </button>
      <button
        type="button"
        :class="$style.key"
        data-testid="numeric-keyboard-digit-0"
        @click="addDigit(0)"
      >
        0
      </button>
      <button
        type="button"
        :class="[$style.key, $style.enter]"
        data-testid="numeric-keyboard-enter"
        @click="submitCode"
      >
        {{ enterLabel }}
      </button>
    </div>
  </UiPopup>
</template>

<script setup lang="ts">
import { shallowRef } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via tag <UiPopup> in <template>
import UiPopup from '../overlays/UiPopup.vue';

interface Props {
  title: string;
  pin: string | number;
  errorMessage: string;
  clearLabel: string;
  enterLabel: string;
  maxLength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  maxLength: 16,
});

const emit = defineEmits<{
  success: [];
  error: [];
  close: [];
}>();

const inputValue = shallowRef('');
const error = shallowRef(false);

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function addDigit(digit: number): void {
  if (inputValue.value.length >= props.maxLength) {
    return;
  }
  inputValue.value += String(digit);
  error.value = false;
}

function clearInput(): void {
  inputValue.value = '';
  error.value = false;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function submitCode(): void {
  if (inputValue.value === String(props.pin)) {
    emit('success');
    clearInput();
  } else {
    error.value = true;
    inputValue.value = '';
    emit('error');
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onClose(): void {
  emit('close');
}
</script>

<style module>
.root {
  font-family: var(--Global--font);
}

.code {
  font-size: 1.5rem;
  width: 100%;
  margin-bottom: 1rem;
  margin-top: 0.5rem;
  height: 3rem;
  padding: 0 2rem;
  text-align: center;
  letter-spacing: 0.1rem;
}

.error {
  color: #ff3b30;
  text-align: center;
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.keypad {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
}

.key {
  background-color: var(--Kiosk--tint-inverse-color);
  color: var(--Kiosk--tint-color);
  border: none;
  border-radius: 0.3rem;
  font-size: 1.5rem;
  height: 3rem;
  margin-bottom: 0.7rem;
  width: 5.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.key:hover {
  background-color: rgba(var(--Kiosk--tint-inverse-color-rgb), 0.8);
}

.clear,
.enter {
  font-size: 1rem;
}
</style>
